import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Star, Eye, EyeOff, X, Upload, User } from 'lucide-react';
import { toast } from 'sonner';
import ImageUpload from '../../components/ImageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminHomepageReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_photo: '',
    rating: 5,
    comment: '',
    location: '',
    is_visible: true,
    display_order: 0
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/homepage-reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(response.data.reviews || []);
    } catch (error) {
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingReview(null);
    setFormData({
      customer_name: '',
      customer_photo: '',
      rating: 5,
      comment: '',
      location: '',
      is_visible: true,
      display_order: reviews.length
    });
    setShowModal(true);
  };

  const openEditModal = (review) => {
    setEditingReview(review);
    setFormData({
      customer_name: review.customer_name || '',
      customer_photo: review.customer_photo || '',
      rating: review.rating || 5,
      comment: review.comment || '',
      location: review.location || '',
      is_visible: review.is_visible !== false,
      display_order: review.display_order || 0
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.customer_name || !formData.comment) {
      toast.error('Name and comment are required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      if (editingReview) {
        await axios.put(`${API}/admin/homepage-reviews/${editingReview.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Review updated');
      } else {
        await axios.post(`${API}/admin/homepage-reviews`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Review created');
      }
      
      setShowModal(false);
      loadReviews();
    } catch (error) {
      toast.error('Failed to save review');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (reviewId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/homepage-reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Review deleted');
      setShowDeleteConfirm(null);
      loadReviews();
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const toggleVisibility = async (review) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/homepage-reviews/${review.id}`, {
        is_visible: !review.is_visible
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadReviews();
      toast.success(`Review ${!review.is_visible ? 'visible' : 'hidden'}`);
    } catch (error) {
      toast.error('Failed to update review');
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'text-gold fill-gold' : 'text-gray-300'}`}
      />
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-homepage-reviews">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-light text-charcoal">Homepage Reviews</h1>
          <p className="text-charcoal-light text-sm mt-1">Manage testimonials shown on homepage</p>
        </div>
        <button
          onClick={openAddModal}
          className="flex items-center gap-2 bg-gold text-white px-4 py-2 text-sm font-medium hover:bg-gold-light transition-colors"
          data-testid="add-review-btn"
        >
          <Plus className="w-4 h-4" />
          Add Review
        </button>
      </div>

      {/* Reviews Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {reviews.length > 0 ? (
          reviews.map((review) => (
            <div
              key={review.id}
              className={`bg-white border ${review.is_visible ? 'border-charcoal/10' : 'border-red-200 bg-red-50/30'} p-6`}
            >
              {/* Customer Info */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {review.customer_photo ? (
                    <img
                      src={review.customer_photo}
                      alt={review.customer_name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-beige flex items-center justify-center">
                      <User className="w-6 h-6 text-charcoal-light" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-medium text-charcoal">{review.customer_name}</h3>
                    {review.location && (
                      <p className="text-xs text-charcoal-light">{review.location}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => toggleVisibility(review)}
                    className={`p-1.5 ${review.is_visible ? 'text-green-600' : 'text-gray-400'}`}
                  >
                    {review.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => openEditModal(review)}
                    className="p-1.5 text-charcoal-light hover:text-gold"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(review.id)}
                    className="p-1.5 text-charcoal-light hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Rating */}
              <div className="flex gap-0.5 mb-3">
                {renderStars(review.rating)}
              </div>

              {/* Comment */}
              <p className="text-sm text-charcoal-light line-clamp-4">"{review.comment}"</p>

              {/* Status Badge */}
              {!review.is_visible && (
                <span className="inline-block mt-3 px-2 py-1 bg-red-100 text-red-700 text-xs">Hidden</span>
              )}
            </div>
          ))
        ) : (
          <div className="col-span-full bg-white border border-charcoal/10 p-12 text-center">
            <Star className="w-12 h-12 mx-auto text-charcoal-light mb-4" />
            <h3 className="font-medium text-charcoal">No reviews yet</h3>
            <p className="text-charcoal-light text-sm mt-1">Add testimonials to display on your homepage</p>
            <button
              onClick={openAddModal}
              className="mt-4 bg-gold text-white px-4 py-2 text-sm font-medium hover:bg-gold-light transition-colors"
            >
              Add First Review
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/50">
          <div className="bg-white w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-charcoal/10 p-6 flex items-center justify-between">
              <h2 className="font-heading text-xl font-light text-charcoal">
                {editingReview ? 'Edit Review' : 'Add Review'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-charcoal-light hover:text-charcoal">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-5">
              {/* Customer Name */}
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                  Customer Name *
                </label>
                <input
                  type="text"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  className="w-full h-11 px-4 border border-charcoal/10 focus:border-gold outline-none"
                  placeholder="John Doe"
                />
              </div>

              {/* Location */}
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                  Location
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full h-11 px-4 border border-charcoal/10 focus:border-gold outline-none"
                  placeholder="Mumbai, India"
                />
              </div>

              {/* Customer Photo */}
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                  Customer Photo (Optional)
                </label>
                <ImageUpload
                  onImageUploaded={(url) => setFormData({ ...formData, customer_photo: url })}
                  currentImage={formData.customer_photo}
                />
              </div>

              {/* Rating */}
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                  Rating
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData({ ...formData, rating: star })}
                      className="p-1"
                    >
                      <Star
                        className={`w-8 h-8 ${star <= formData.rating ? 'text-gold fill-gold' : 'text-gray-300'}`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Comment */}
              <div>
                <label className="block text-xs tracking-wider uppercase text-charcoal-light font-medium mb-2">
                  Review Comment *
                </label>
                <textarea
                  value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  className="w-full h-32 px-4 py-3 border border-charcoal/10 focus:border-gold outline-none resize-none"
                  placeholder="The quality is exceptional and the fit is perfect..."
                />
              </div>

              {/* Visible Toggle */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_visible}
                  onChange={(e) => setFormData({ ...formData, is_visible: e.target.checked })}
                  className="w-5 h-5 accent-gold"
                />
                <span className="text-sm text-charcoal">Show on homepage</span>
              </label>
            </div>
            
            <div className="sticky bottom-0 bg-white border-t border-charcoal/10 p-6 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 border border-charcoal/20 text-charcoal text-sm font-medium hover:bg-beige transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-gold text-white text-sm font-medium hover:bg-gold-light disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving...' : editingReview ? 'Update Review' : 'Add Review'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/50">
          <div className="bg-white p-6 max-w-sm w-full">
            <h3 className="font-heading text-lg font-light text-charcoal mb-2">Delete Review?</h3>
            <p className="text-charcoal-light text-sm mb-6">This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 border border-charcoal/20 text-charcoal text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHomepageReviews;
